import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useState } from "react";
import { Plus, Circle, CheckCircle2, Clock } from "lucide-react";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";

const PRIO = ["Bassa", "Media", "Alta", "Urgente"];
const PRIO_COLOR = { "Bassa": "text-textmuted", "Media": "text-primary", "Alta": "text-warning", "Urgente": "text-critical" };

export default function TasksPage() {
    const qc = useQueryClient();
    const q = useQuery({ queryKey: ["tasks"], queryFn: () => api.get("/tasks").then((r) => r.data) });
    const [openAdd, setOpenAdd] = useState(false);

    const patch = useMutation({
        mutationFn: ({ id, ...p }) => api.patch(`/tasks/${id}`, p),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
    });
    const create = useMutation({
        mutationFn: (b) => api.post("/tasks", b),
        onSuccess: () => { qc.invalidateQueries({ queryKey: ["tasks"] }); setOpenAdd(false); toast.success("Task creata."); },
    });

    const items = q.data || [];
    const groups = {
        "Da fare": items.filter((t) => t.status !== "Completato"),
        "Completato": items.filter((t) => t.status === "Completato"),
    };

    return (
        <div className="space-y-6 pv-enter">
            <div className="flex items-end justify-between">
                <div>
                    <div className="pv-eyebrow mb-2">Tasks</div>
                    <h1 className="pv-serif text-4xl tracking-tight">{items.length} <span className="text-textmuted">attività</span></h1>
                </div>
                <button
                    data-testid={TID.tasksAdd}
                    onClick={() => setOpenAdd(true)}
                    className="flex items-center gap-1.5 bg-primary text-primary-foreground rounded-xl px-3.5 py-2 text-[13px] font-medium"
                >
                    <Plus size={14} strokeWidth={1.5} /> Nuova task
                </button>
            </div>

            {["Da fare", "Completato"].map((k) => (
                <div key={k}>
                    <div className="pv-eyebrow mb-3">{k} · {groups[k].length}</div>
                    <div className="pv-card divide-y divide-border/[var(--border-opacity)]">
                        {groups[k].map((t) => (
                            <div key={t.id} className="flex items-center gap-3 px-5 py-3.5">
                                <button
                                    onClick={() => patch.mutate({ id: t.id, status: t.status === "Completato" ? "Da fare" : "Completato" })}
                                    className="flex-shrink-0"
                                >
                                    {t.status === "Completato"
                                        ? <CheckCircle2 size={16} strokeWidth={1.5} className="text-success" />
                                        : <Circle size={16} strokeWidth={1.5} className="text-textsubtle hover:text-primary transition-colors" />}
                                </button>
                                <div className="flex-1 min-w-0">
                                    <div className={`text-[13.5px] font-medium ${t.status === "Completato" ? "line-through text-textsubtle" : ""}`}>{t.title}</div>
                                    <div className="text-[11px] text-textsubtle mt-0.5 flex items-center gap-2">
                                        <span>{t.assignee}</span>
                                        {t.client_name && <><span>·</span><span>{t.client_name}</span></>}
                                        {t.due_date && <><span>·</span><Clock size={10} strokeWidth={1.5} /><span>{new Date(t.due_date).toLocaleDateString("it-IT")}</span></>}
                                    </div>
                                </div>
                                <span className={`text-[11px] font-medium ${PRIO_COLOR[t.priority] || "text-textmuted"}`}>{t.priority}</span>
                            </div>
                        ))}
                        {groups[k].length === 0 && <div className="px-5 py-8 text-center text-textsubtle text-[12.5px]">Nessuna task</div>}
                    </div>
                </div>
            ))}

            {openAdd && <AddDialog onClose={() => setOpenAdd(false)} onSave={(b) => create.mutate(b)} busy={create.isPending} />}
        </div>
    );
}

function AddDialog({ onClose, onSave, busy }) {
    const [form, setForm] = useState({ title: "", priority: "Media", assignee: "Team", due_date: "" });
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
            <div className="absolute inset-0 bg-navy/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-md pv-card p-6 pv-enter">
                <div className="pv-eyebrow mb-2">Nuova task</div>
                <h3 className="pv-serif text-2xl mb-5">Cosa devi fare?</h3>
                <div className="space-y-3">
                    <F label="Titolo" v={form.title} on={set("title")} />
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <div className="pv-eyebrow mb-1">Priorità</div>
                            <select value={form.priority} onChange={set("priority")} className="w-full rounded-lg border border-border/[var(--border-opacity)] bg-surface px-3 py-2 text-[13px]">
                                {PRIO.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>
                        <F label="Assegnata a" v={form.assignee} on={set("assignee")} />
                    </div>
                    <F label="Scadenza (opzionale)" v={form.due_date} on={set("due_date")} type="date" />
                </div>
                <div className="mt-6 flex justify-end gap-2">
                    <button onClick={onClose} className="px-3 py-2 text-[13px] text-textmuted">Annulla</button>
                    <button onClick={() => onSave(form)} disabled={busy || !form.title} className="px-3.5 py-2 text-[13px] rounded-lg bg-primary text-primary-foreground disabled:opacity-50">
                        {busy ? "..." : "Crea"}
                    </button>
                </div>
            </div>
        </div>
    );
}
function F({ label, v, on, type = "text" }) {
    return (
        <label className="block">
            <div className="pv-eyebrow mb-1">{label}</div>
            <input type={type} value={v} onChange={on} className="w-full rounded-lg border border-border/[var(--border-opacity)] bg-surface px-3 py-2 text-[13px] outline-none focus:border-primary focus:pv-ring-primary" />
        </label>
    );
}
